/**
 * Pure model of the composer's Search Console band (Prompt Studio lot H step 13).
 * No React, no API calls: the per-brand band state, its reducer and the views
 * derived from it. The store and the calls live in searchConsoleStore.ts.
 *
 * @see docs/plans/prompt-studio-build/H-search-console.md step 13, UI-SPEC §2.13
 */
import type { GscCallbackResult, GscImportResponse, GscQueryRow, GscStatus } from '@traaker/shared';
import { GSC_PROPOSE_MAX, normalizeDomain } from '@traaker/shared';

/** A proposal for one query: the text, or `null` when the seed came back without one. */
export type GscProposal = string | null;

/** The one-shot line above the band (callback return, a failed « Autoriser »). */
export type GscLine = 'callbackError' | 'scopeMissing' | 'wrongUser';

/** The band's per-brand state. Survives navigation and popup close for the page session. */
export interface GscBrandState {
  status: GscStatus | null;
  statusLoading: boolean;
  /** The last import and the property it read: the cost-rule cache (an import is never re-run while it matches). */
  imported: { siteUrl: string; res: GscImportResponse } | null;
  /** Import even with a cache: right after `?gsc=connected` (step 13). A PUT property imports by itself. */
  reimport: boolean;
  importing: boolean;
  /** `importLimited` = 429 on import (the cache stays shown); `unavailable` = an outage (nothing listed). */
  error: 'importLimited' | 'unavailable' | null;
  /** Keyed by query. Absent = never asked; `null` = no proposal. */
  proposals: Record<string, GscProposal>;
  /** Queries in the from-seeds call in flight (spinner, not checkable). */
  proposing: string[];
  /** Queries of the last failed from-seeds call (« Réessayer » re-runs them). */
  failedSeeds: string[];
  checked: string[];
  /**
   * The draft as the user edited it (owner 2026-10-29: the draft is editable). `null` = not edited, the draft is
   * derived from the checked rows. Once edited, checking a row appends its proposal and unchecking one removes the
   * lines still equal to it; a line the user rewrote stays theirs.
   */
  draft: string | null;
  /** « Changer » opened the picker (only when the status carries candidates). */
  picking: boolean;
  /** PUT property in flight. */
  choosing: string | null;
  /** POST authorize in flight. */
  authorizing: boolean;
  line: GscLine | null;
}

export const INITIAL_GSC: GscBrandState = {
  status: null,
  statusLoading: false,
  imported: null,
  reimport: false,
  importing: false,
  error: null,
  proposals: {},
  proposing: [],
  failedSeeds: [],
  checked: [],
  draft: null,
  picking: false,
  choosing: null,
  authorizing: false,
  line: null,
};

const CALLBACK_RESULTS: readonly GscCallbackResult[] = ['connected', 'cancelled', 'expired', 'error', 'scope_missing', 'wrong_user'];

export function isGscCallbackResult(v: string | null): v is GscCallbackResult {
  return !!v && (CALLBACK_RESULTS as readonly string[]).includes(v);
}

/** The line a callback result shows once on return (`connected` / `cancelled` show none). */
export function callbackLine(r: GscCallbackResult): GscLine | null {
  if (r === 'expired' || r === 'error') return 'callbackError';
  if (r === 'scope_missing') return 'scopeMissing';
  if (r === 'wrong_user') return 'wrongUser';
  return null;
}

export type GscAction =
  /** The band opened after an OAuth return (`?gsc=`). `connected` imports again, cache or not. */
  | { type: 'returned'; result: GscCallbackResult }
  /** The band opened without a return: an earlier callback line is not shown again. */
  | { type: 'opened' }
  | { type: 'statusStart' }
  | { type: 'statusOk'; status: GscStatus }
  | { type: 'statusFailed' }
  | { type: 'importStart' }
  | { type: 'importOk'; siteUrl: string; res: GscImportResponse }
  | { type: 'importFailed'; error: 'importLimited' | 'unavailable' }
  | { type: 'proposeStart'; seeds: string[] }
  | { type: 'proposeOk'; seeds: string[]; proposals: { seed: string; text?: string }[] }
  | { type: 'proposeFailed'; seeds: string[] }
  | { type: 'toggle'; query: string }
  | { type: 'checkAll'; on: boolean }
  | { type: 'editDraft'; text: string }
  | { type: 'pick'; on: boolean }
  | { type: 'chooseStart'; siteUrl: string }
  | { type: 'chooseOk'; status: GscStatus }
  | { type: 'chooseFailed' }
  | { type: 'authorizeStart' }
  | { type: 'authorizeEnd'; failed: boolean };

const without = (xs: string[], drop: string[]) => {
  const d = new Set(drop);
  return xs.filter((x) => !d.has(x));
};

export function gscReducer(s: GscBrandState, a: GscAction): GscBrandState {
  const next = reduce(s, a);
  // The checked set or a proposal changed under an edited draft: carry the change into the text.
  return next !== s && next.draft !== null && SYNCS_DRAFT.has(a.type) ? syncDraft(s, next) : next;
}

const SYNCS_DRAFT = new Set<GscAction['type']>(['toggle', 'checkAll', 'proposeOk', 'proposeFailed']);

/** Lines of `next`'s derived draft that `prev` did not have are appended; lines `prev` derived that `next` no longer does are removed. */
function syncDraft(prev: GscBrandState, next: GscBrandState): GscBrandState {
  const before = new Set(derivedDraftLines(prev));
  const after = derivedDraftLines(next);
  const kept = new Set(after);
  const lines = (next.draft ?? '').split('\n').filter((l) => !(before.has(l.trim()) && !kept.has(l.trim())));
  while (lines.length && !lines[lines.length - 1].trim()) lines.pop();
  const present = new Set(lines.map((l) => l.trim()));
  for (const l of after) if (!before.has(l) && !present.has(l)) lines.push(l);
  return { ...next, draft: lines.join('\n') };
}

function reduce(s: GscBrandState, a: GscAction): GscBrandState {
  switch (a.type) {
    case 'returned':
      return {
        ...s,
        line: callbackLine(a.result),
        error: null,
        picking: false,
        ...(a.result === 'connected' ? { reimport: true } : {}),
      };
    case 'opened':
      // Lines of an earlier open are not shown again.
      return s.line || s.error ? { ...s, line: null, error: null } : s;
    case 'statusStart':
      return { ...s, statusLoading: true };
    case 'statusOk':
      return {
        ...s,
        status: a.status,
        statusLoading: false,
        ...(a.status.state === 'connected' ? {} : { imported: null, picking: false }),
      };
    case 'statusFailed':
      // A failed reload keeps a status already on screen; with none, the band says so.
      return { ...s, statusLoading: false, ...(s.status ? {} : { error: 'unavailable' as const }) };
    case 'importStart':
      // The cache of this property stays on screen until the new result (a 429 keeps it).
      return { ...s, importing: true, reimport: false, error: null };
    case 'importOk': {
      const base = { ...s, importing: false, error: null };
      if (a.res.state === 'ready') {
        return { ...base, imported: { siteUrl: a.siteUrl, res: a.res }, proposals: {}, proposing: [], failedSeeds: [], checked: [], draft: null };
      }
      if (a.res.state === 'no_data') return { ...base, imported: { siteUrl: a.siteUrl, res: a.res } };
      // revoked / lost access / not connected / needs a property: the status changed under us.
      return { ...base, imported: null, status: { ...(s.status ?? {}), state: a.res.state } };
    }
    case 'importFailed':
      return {
        ...s,
        importing: false,
        error: a.error,
        // An outage never shows a stale list; a 429 keeps the cached one under its line.
        ...(a.error === 'unavailable' ? { imported: null } : {}),
      };
    case 'proposeStart':
      return { ...s, proposing: [...s.proposing, ...a.seeds] };
    case 'proposeOk': {
      const bySeed = new Map(a.proposals.map((p) => [p.seed, p.text?.trim() || null]));
      // Every seed empty: the generator failed (its catch answers no text at all), not « no proposal ».
      if (a.seeds.every((q) => !bySeed.get(q))) return gscReducer(s, { type: 'proposeFailed', seeds: a.seeds });
      const proposals = { ...s.proposals };
      for (const q of a.seeds) proposals[q] = bySeed.get(q) ?? null;
      const withText = a.seeds.filter((q) => proposals[q]);
      return {
        ...s,
        proposals,
        proposing: without(s.proposing, a.seeds),
        failedSeeds: without(s.failedSeeds, a.seeds),
        // Rows arrive checked; a row with no proposal is unchecked.
        checked: [...without(s.checked, a.seeds), ...withText],
      };
    }
    case 'proposeFailed':
      return {
        ...s,
        proposing: without(s.proposing, a.seeds),
        failedSeeds: [...without(s.failedSeeds, a.seeds), ...a.seeds],
        checked: without(s.checked, a.seeds),
      };
    case 'toggle': {
      if (s.proposing.includes(a.query) || s.proposals[a.query] === null) return s;
      const on = s.checked.includes(a.query);
      return { ...s, checked: on ? without(s.checked, [a.query]) : [...s.checked, a.query] };
    }
    case 'checkAll': {
      // « Tout cocher » checks the rows that have a proposal; a row without one is asked for by its own click.
      const rows = readyRows(s) ?? [];
      return {
        ...s,
        checked: a.on ? [...new Set([...s.checked, ...rows.filter((r) => s.proposals[r.query]).map((r) => r.query)])] : [],
      };
    }
    case 'editDraft':
      return { ...s, draft: a.text };
    case 'pick':
      return { ...s, picking: a.on };
    case 'chooseStart':
      return { ...s, choosing: a.siteUrl, error: null };
    case 'chooseOk':
      // The store imports right after (a cache of ANOTHER property is never shown: currentImport).
      return { ...s, choosing: null, picking: false, status: a.status, error: null };
    case 'chooseFailed':
      return { ...s, choosing: null, error: 'unavailable' };
    case 'authorizeStart':
      return { ...s, authorizing: true, line: null };
    case 'authorizeEnd':
      return { ...s, authorizing: false, line: a.failed ? 'callbackError' : s.line };
    default:
      return s;
  }
}

// ---------------------------------------------------------------------------
// Derived views
// ---------------------------------------------------------------------------

/** The cached import of the CURRENT property, if any. */
export function currentImport(s: GscBrandState): GscImportResponse | null {
  const site = s.status?.state === 'connected' ? s.status.siteUrl ?? '' : null;
  return site !== null && s.imported?.siteUrl === site ? s.imported.res : null;
}

/** Rows of a `ready` import of the current property, by impressions desc; null otherwise. */
export function readyRows(s: GscBrandState): GscQueryRow[] | null {
  const res = currentImport(s);
  if (res?.state !== 'ready') return null;
  return [...res.rows].sort((a, b) => b.impressions - a.impressions);
}

export type GscView =
  | { kind: 'loading' }
  | { kind: 'unavailable' }
  | { kind: 'off' }
  | { kind: 'revoked' }
  | { kind: 'accessLost' }
  | { kind: 'picker'; candidates: { siteUrl: string; permissionLevel: string }[] }
  | { kind: 'importing'; siteUrl: string }
  | { kind: 'connected'; siteUrl: string }
  | { kind: 'empty'; siteUrl: string }
  | { kind: 'ready'; siteUrl: string; rows: GscQueryRow[] };

export function gscView(s: GscBrandState): GscView {
  if (!s.status) return s.error === 'unavailable' ? { kind: 'unavailable' } : { kind: 'loading' };
  const candidates = s.status.candidates ?? [];
  switch (s.status.state) {
    case 'not_connected': return { kind: 'off' };
    case 'revoked': return { kind: 'revoked' };
    case 'property_access_lost': return s.picking && candidates.length ? { kind: 'picker', candidates } : { kind: 'accessLost' };
    case 'needs_property': return { kind: 'picker', candidates };
    case 'connected': {
      if (s.picking && candidates.length) return { kind: 'picker', candidates };
      const siteUrl = s.status.siteUrl ?? '';
      const res = currentImport(s);
      if (res?.state === 'ready') return { kind: 'ready', siteUrl, rows: readyRows(s)! };
      if (res?.state === 'no_data') return { kind: 'empty', siteUrl };
      return s.importing ? { kind: 'importing', siteUrl } : { kind: 'connected', siteUrl };
    }
    default: return { kind: 'loading' };
  }
}

/**
 * The draft derived from the checked rows: their proposed prompts, one per line, in list
 * order. A checked row still waiting for its proposal adds nothing, and a query is never a line.
 */
function derivedDraftLines(s: GscBrandState): string[] {
  const on = new Set(s.checked);
  return (readyRows(s) ?? []).filter((r) => on.has(r.query) && s.proposals[r.query]).map((r) => s.proposals[r.query] as string);
}

/** The draft field's text: the user's edit, else the derived lines. `null` when the band is not `ready`. */
export function gscDraftText(s: GscBrandState): string | null {
  if (gscView(s).kind !== 'ready') return null;
  return s.draft ?? derivedDraftLines(s).join('\n');
}

/** The lines « Vérifier » sends (non-empty, trimmed). `null` when the band is not `ready` (no draft, no « Vérifier »). */
export function gscDraftLines(s: GscBrandState): string[] | null {
  const text = gscDraftText(s);
  return text === null ? null : text.split('\n').map((l) => l.trim()).filter(Boolean);
}

/** The first proposal call after a `ready` import: the top GSC_PROPOSE_MAX queries by impressions. */
export function firstSeeds(rows: GscQueryRow[]): string[] {
  return [...rows].sort((a, b) => b.impressions - a.impressions).slice(0, GSC_PROPOSE_MAX).map((r) => r.query);
}

/**
 * Checked rows with no proposal yet (beyond the first 50, or a failed one checked
 * again), ≤ GSC_PROPOSE_MAX; none while a call runs. A failed call unchecks its rows,
 * so this never loops on its own.
 */
export function pendingSeeds(s: GscBrandState): string[] {
  if (s.proposing.length) return [];
  return s.checked.filter((q) => !(q in s.proposals)).slice(0, GSC_PROPOSE_MAX);
}

// ---------------------------------------------------------------------------
// Properties
// ---------------------------------------------------------------------------

export function propertyType(siteUrl: string): 'domain' | 'prefix' {
  return siteUrl.startsWith('sc-domain:') ? 'domain' : 'prefix';
}

/** `sc-domain:x` → `x`; `https://www.x.fr/` → `www.x.fr` (http and paths kept). */
export function propertyName(siteUrl: string): string {
  return siteUrl.replace(/^sc-domain:/, '').replace(/^https:\/\//, '').replace(/\/$/, '');
}

/** The property carries the brand's domain: its host equals the brand host or is a parent of it (step 8). */
export function isBrandProperty(siteUrl: string, brandUrl: string | undefined): boolean {
  const brandHost = normalizeDomain(brandUrl);
  if (!brandHost) return false;
  const host = normalizeDomain(siteUrl.replace(/^sc-domain:/, ''));
  return !!host && (host === brandHost || brandHost.endsWith(`.${host}`));
}
